import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { ClientInferResponseBody } from "@ts-rest/core";
import { getBackendAPIClient } from "../lib/backendAPIClient";
import { ruleContract } from "../common/contracts/rule.contract";
import { useWorkspaceContext } from "../state/workspaceContext";
import {
    RuleConditionType,
    RuleSource,
    RuleConditionAmount,
    TransactionRuleGroup,
    TransactionRule,
    RuleConditionDate,
} from "../common/types/transactionRule";
import { queryClient } from "../queryClient";
import { TransactionRuleGroupDto } from "../common/dto/transactionRule/transaction-rule-group.dto";
import { TransactionRuleDto } from "../common/dto/transactionRule/transaction-rule.dto";
import { Counterparty } from "../common/types/counterparty";

const getRuleGroupsQueryKey = (workspaceId: string) => [
    "rule-groups",
    workspaceId,
];

export const rulesContractClient = getBackendAPIClient(ruleContract);

export function useWorkspaceRuleGroups() {
    const { activeWorkspaceId } = useWorkspaceContext();

    return useQuery({
        queryKey: getRuleGroupsQueryKey(activeWorkspaceId ?? ""),
        queryFn: async () => {
            if (!activeWorkspaceId) {
                return undefined;
            }

            const response = await rulesContractClient.getRuleGroups({
                params: {
                    workspaceId: activeWorkspaceId,
                },
            });

            return {
                ...response.body,
                ruleGroups: response.body.ruleGroups.map((group) =>
                    TransactionRuleGroupDto.deserialize(group),
                ),
            };
        },
        staleTime: 2 * 60 * 1000,
    });
}

export function useDefaultAccountingRules() {
    const { activeWorkspaceId } = useWorkspaceContext();

    return useQuery({
        queryKey: ["default-accounting-rules", activeWorkspaceId],
        queryFn: async () => {
            if (!activeWorkspaceId) {
                return undefined;
            }

            const response =
                await rulesContractClient.getAccountingDefaultRules({
                    params: {
                        workspaceId: activeWorkspaceId,
                    },
                });

            return {
                ruleGroup: TransactionRuleGroupDto.deserialize(
                    response.body.ruleGroup,
                ),
            };
        },
        staleTime: 2 * 60 * 1000,
    });
}

interface UpdateRulePositionParams {
    workspaceId: string;
    rule: TransactionRule;
}

const moveRule = (
    ruleGroups: TransactionRuleGroup[],
    movedRule: TransactionRule,
) =>
    ruleGroups.map((group) => {
        // we are moving the rule here
        if (group.id === movedRule.groupId) {
            const newRules = (group.rules ?? []).map((rule) => {
                if (rule.id === movedRule.id) {
                    return {
                        ...rule,
                        order: movedRule.order,
                    };
                }
                return rule;
            });

            // if it is moved to a new group, we need to add it manually
            if (newRules.every((rule) => rule.id !== movedRule.id)) {
                newRules.push(movedRule);
            }

            return {
                ...group,
                rules: newRules.toSorted((a, b) => a.order - b.order),
            };
        }
        // we remove from any other group it might be at
        return {
            ...group,
            rules: group.rules?.filter((rule) => rule.id !== movedRule.id),
        };
    });

export const useUpdateRulePosition = () => {
    const { activeWorkspaceId } = useWorkspaceContext();
    const queryKey = getRuleGroupsQueryKey(activeWorkspaceId ?? "");

    return useMutation({
        mutationFn: async ({ workspaceId, rule }: UpdateRulePositionParams) => {
            const response = await rulesContractClient.changeRuleOrder({
                params: {
                    workspaceId,
                    ruleId: rule.id,
                },
                body: {
                    groupId: rule.groupId,
                    order: rule.order,
                },
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onMutate: async ({ rule }: UpdateRulePositionParams) => {
            await queryClient.cancelQueries({ queryKey });

            const previousRuleGroups = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            const updatedRuleGroups = moveRule(
                previousRuleGroups?.ruleGroups ?? [],
                rule,
            );

            queryClient.setQueryData(queryKey, {
                ...previousRuleGroups,
                ruleGroups: updatedRuleGroups,
            });

            return { previousRuleGroups };
        },
        onError: (_, __, context) => {
            // we need to find the previous rule's position and move it back
            queryClient.setQueryData(
                queryKey,
                context?.previousRuleGroups ?? [],
            );
        },
    });
};

interface RuleData {
    categoryId?: string | null;
    entityId?: number | null;
    classId?: string | null;
    accountId?: string | null;
    isDisabled?: boolean;
    order?: number;
    groupId?: string;
    conditions?: Array<{
        conditionType: RuleConditionType;
        value:
            | string
            | string[]
            | number
            | number[]
            | RuleConditionAmount
            | RuleConditionDate;
    }>;
}

export interface CreateRuleParams {
    workspaceId: string;
    rule: RuleData;
    source: RuleSource;
    applyToExistingTransactions: boolean;
    excludedTransactionsIds?: number[];
}

export const useCreateRule = (onSuccess?: () => void) =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            rule,
            source,
            applyToExistingTransactions,
            excludedTransactionsIds = [],
        }: CreateRuleParams) => {
            const response = await rulesContractClient.createRule({
                params: {
                    workspaceId,
                },
                body: {
                    rule,
                    source,
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                },
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            // Update the query cache with the new rule
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
                counterparties: Counterparty[];
                transactionCounts: Record<string, number>;
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === variables.rule.groupId) {
                            return {
                                ...group,
                                rules: [...(group.rules ?? []), data],
                            };
                        }
                        return group;
                    },
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            onSuccess?.();
        },
    });

interface UpdateRuleParams extends Omit<CreateRuleParams, "source"> {
    ruleId: string;
}

export const useUpdateRule = (onSuccess?: () => void) =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            ruleId,
            rule,
            applyToExistingTransactions,
            excludedTransactionsIds = [],
        }: UpdateRuleParams) => {
            const response = await rulesContractClient.updateRule({
                params: {
                    workspaceId,
                    ruleId,
                },
                body: {
                    rule,
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                },
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            // Update the query cache with the updated rule
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === variables.rule.groupId) {
                            return {
                                ...group,
                                rules: (group.rules ?? []).map((rule) =>
                                    rule.id === variables.ruleId ? data : rule,
                                ),
                            };
                        }
                        return group;
                    },
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            onSuccess?.();
        },
    });

interface RuleRemovalOptions {
    onSuccess?: () => void;
    onError?: (error: RuleRemovalError) => void;
}

export type RuleRemovalError = ClientInferResponseBody<
    typeof ruleContract.deleteRule,
    400
>;

export function useRuleRemovalMutation(
    rule: TransactionRule,
    { onSuccess, onError }: RuleRemovalOptions = {},
) {
    return useMutation({
        mutationFn: () =>
            rulesContractClient.deleteRule({
                params: {
                    workspaceId: rule.workspaceId,
                    ruleId: rule.id,
                },
            }),

        onSuccess: () => {
            const queryKey = getRuleGroupsQueryKey(rule.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === rule.groupId) {
                            return {
                                ...group,
                                rules: (group.rules ?? []).filter(
                                    (r) => r.id !== rule.id,
                                ),
                            };
                        }
                        return group;
                    },
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            onSuccess?.();
        },
        onError: (error) => {
            if (
                axios.isAxiosError(error) &&
                error.response?.status === 400 &&
                typeof error.response.data === "object" &&
                !("message" in error.response.data)
            ) {
                onError?.(error.response.data as RuleRemovalError);
            }
        },
    });
}
